import { useKeyboardFocusDetect } from '@magicschool/spellbook/hooks/useKeyboardFocusDetect';
import { FOCUSABLE_ELEMENTS_SELECTOR, getFirstFocusableElement } from '@magicschool/spellbook/lib/focus';
import type { Dispatch, RefObject, SyntheticEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { findNextFocusableElement } from './helpers';

export type ToggleTipHandlers = {
  handleOnOpenChange: (open: boolean) => void;
  handleOnClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleOnKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  handleOnPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  handleOnPointerEnter: (event: React.PointerEvent<HTMLButtonElement>) => void;
  handleOnPointerLeave: (event: React.PointerEvent<HTMLButtonElement>) => void;
  handleOnFocus: (event: React.FocusEvent<HTMLButtonElement>) => void;
  handleOnBlur: (event: React.FocusEvent<HTMLButtonElement>) => void;
  handleOnPointerDownOutside: (event: CustomEvent<{ originalEvent: PointerEvent }>) => void;
  handleOnEscapeKeyDown: () => void;
  handleOnFocusContent: (event: React.FocusEvent<HTMLDivElement>) => void;
  handleOnBlurContent: (event: React.FocusEvent<HTMLDivElement>) => void;
};

export type ToggleTipHandlersProps = {
  onOpenChange?: (open: boolean) => void;
  onOpen?: (open: boolean) => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  open: boolean;
  setOpen: Dispatch<React.SetStateAction<boolean>>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
};

const disableDefaultBehavior = <T extends SyntheticEvent>(event: T): T => {
  event.preventDefault();
  event.stopPropagation();
  return event;
};

export const useToggleTipHandlers = ({
  onOpenChange,
  open: internalOpen,
  setOpen: setInternalOpen,
  onClick,
  triggerRef,
  contentRef,
}: ToggleTipHandlersProps): ToggleTipHandlers => {
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);
  const isKeyboardRef = useKeyboardFocusDetect();

  const handleOnOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const toggleOpen = useCallback(
    (open: boolean) => {
      setInternalOpen(open);
      handleOnOpenChange(open);
    },
    [setInternalOpen, handleOnOpenChange],
  );

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      toggleOpen(!internalOpen);
      onClick?.(event);
    },
    [toggleOpen, internalOpen, onClick],
  );

  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleOpen(!internalOpen);
      }

      if (event.key === 'Tab' && event.shiftKey && triggerRef.current) {
        event.preventDefault();
        const previousFocusableElement = findNextFocusableElement(triggerRef.current, true);

        if (previousFocusableElement) {
          previousFocusableElement.focus();
          if (internalOpen) {
            toggleOpen(false);
          }
        }
        return;
      }

      if (event.key === 'Tab' && internalOpen) {
        cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
        cleanupFunctionsRef.current = [];

        const focusableElements = contentRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR);
        const firstFocusableElement = getFirstFocusableElement(contentRef.current);
        if (firstFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();

          focusableElements?.forEach((element) => {
            const keydownHandler = (e: KeyboardEvent) => {
              if (e.key === 'Tab') {
                e.preventDefault();

                if (e.shiftKey) {
                  const currentElementIndex = Array.from(focusableElements || []).indexOf(element);

                  if (currentElementIndex === 0) {
                    if (triggerRef.current) {
                      triggerRef.current.focus();
                    }
                    toggleOpen(false);

                    return;
                  } else {
                    const previousElement = focusableElements?.[currentElementIndex - 1];
                    if (previousElement) {
                      previousElement.focus();
                    }
                  }

                  return;
                }

                const nextElement = element.nextElementSibling as HTMLElement;
                if (nextElement && focusableElements && Array.from(focusableElements).includes(nextElement)) {
                  nextElement.focus();
                } else {
                  if (triggerRef.current) {
                    const focusableChildrenOfTrigger = triggerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR);

                    let nextFocusableElement: HTMLElement | null = null;

                    if (focusableChildrenOfTrigger.length > 0) {
                      const lastFocusableChild = focusableChildrenOfTrigger[focusableChildrenOfTrigger.length - 1];
                      if (lastFocusableChild) {
                        nextFocusableElement = findNextFocusableElement(lastFocusableChild);
                      }
                    } else {
                      nextFocusableElement = findNextFocusableElement(triggerRef.current);
                    }

                    if (nextFocusableElement) {
                      nextFocusableElement.focus();
                    } else {
                      if (focusableChildrenOfTrigger?.[0]) {
                        focusableChildrenOfTrigger[0].focus();
                      } else {
                        triggerRef.current.focus();
                      }
                    }
                  }
                  toggleOpen(false);
                }
              }
            };

            element.addEventListener('keydown', keydownHandler);

            cleanupFunctionsRef.current.push(() => {
              element.removeEventListener('keydown', keydownHandler);
            });
          });
        } else {
          toggleOpen(false);
        }
      }
    },
    [toggleOpen, internalOpen, contentRef, triggerRef],
  );

  const handleOnPointerDownOutside = useCallback(
    (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
      disableDefaultBehavior(event as unknown as SyntheticEvent);
      const isTargetTheTrigger =
        event.detail.originalEvent.target === triggerRef.current || triggerRef.current?.contains(event.detail.originalEvent.target as Node);

      if (isTargetTheTrigger) {
        return;
      }

      toggleOpen(false);
    },
    [toggleOpen],
  );

  const handleOnEscapeKeyDown = useCallback(() => toggleOpen(false), [toggleOpen]);

  const handleOnFocus = useCallback(
    (event: React.FocusEvent<HTMLButtonElement>) => {
      disableDefaultBehavior(event);
      if (isKeyboardRef.current) {
        toggleOpen(true);
      }
    },
    [toggleOpen],
  );

  const handleOnPointerDown = useCallback(disableDefaultBehavior, []);
  const handleOnPointerEnter = useCallback(disableDefaultBehavior, []);
  const handleOnPointerLeave = useCallback(disableDefaultBehavior, []);
  const handleOnBlur = useCallback(disableDefaultBehavior, []);
  const handleOnFocusContent = useCallback(disableDefaultBehavior, []);
  const handleOnBlurContent = useCallback(disableDefaultBehavior, []);

  useEffect(() => {
    return () => {
      cleanupFunctionsRef.current.forEach((cleanup) => cleanup());
      cleanupFunctionsRef.current = [];
    };
  }, []);

  return {
    handleOnOpenChange, // we don't currently need to use this handler in the Tooltip component, but we return it for potential future use
    handleOnClick,
    handleOnKeyDown,
    handleOnPointerDown,
    handleOnPointerEnter,
    handleOnPointerLeave,
    handleOnFocus,
    handleOnBlur,
    handleOnPointerDownOutside,
    handleOnEscapeKeyDown,
    handleOnFocusContent,
    handleOnBlurContent,
  };
};
